import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox as BrCheckbox, Fieldset, Layout } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { NumericFormField } from '../../../../../components/FormFields/NumericFormField';
import { StaticTextFormField } from '../../../../../components/FormFields/StaticTextFormField';
import { TextFormField } from '../../../../../components/FormFields/TextFormField';
import { fuelTypesList } from '../../../../../resources/base/models/vehicleTypes';
import {
  getDisplayedManufacturerLabel,
  isEngineElectric,
  isEngineElectricOrHybrid,
} from '../../../../../services/common/miscUtils';
import { checkCurrentMileageStockAndNew } from './utils';

export const NewVehicleBaseForm: FunctionComponent<{
  co2EmissionsIsSetByAPI: boolean;
  isFormReadonly: boolean;
  setShowDemoCarModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ co2EmissionsIsSetByAPI, isFormReadonly, setShowDemoCarModal }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<any>();

  return (
    <Fieldset>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/4" s="1/1">
            <StaticTextFormField
              fieldName="brand"
              labelTranslationKey="step1:configVehicle:brand"
              isReadonly={!isFormReadonly}
              isDisabled={isFormReadonly}
              displayValue={values.brand ? getDisplayedManufacturerLabel(values.brand) : ''}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <TextFormField
              fieldName="modelGroup"
              labelTranslationKey="step1:configVehicle:modelGroup"
              isReadonly={!isFormReadonly}
              isDisabled={isFormReadonly}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <TextFormField
              fieldName="model"
              labelTranslationKey="step1:configVehicle:model"
              isReadonly={!isFormReadonly}
              isDisabled={isFormReadonly}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <StaticTextFormField
              fieldName="engine"
              isReadonly={!isFormReadonly}
              isDisabled={isFormReadonly}
              labelTranslationKey="step1:configVehicle:engine"
              displayValue={
                values.engine ? (fuelTypesList.find((item) => item.value === values.engine)?.label ?? '') : ''
              }
            />
          </Layout.Item>

          <Layout.Item default="1/1" s="1/1">
            <TextFormField
              fieldName="modelName"
              labelTranslationKey="step1:configVehicle:modelName"
              isReadonly={!isFormReadonly}
              isDisabled={isFormReadonly}
            />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/5" s="1/1">
            <NumericFormField
              fieldName="catalogPriceBto"
              labelTranslationKey={t('step1:configVehicle:catalogPriceBTO')}
              type="money"
              isReadonly={!isFormReadonly}
              isDisabled={isFormReadonly}
              maximumFractionDigits={2}
            />
          </Layout.Item>
          <Layout.Item default="1/5" s="1/1">
            <NumericFormField
              fieldName="co2emision"
              labelTranslationKey={t('step1:configVehicle:co2emission')}
              type="emission"
              isDisabled={isEngineElectric(values?.engine) || co2EmissionsIsSetByAPI || isFormReadonly}
              isReadonly={co2EmissionsIsSetByAPI && !isFormReadonly}
              maximumFractionDigits={0}
            />
          </Layout.Item>
          <Layout.Item default="1/5" s="1/1">
            <NumericFormField
              fieldName="fiscalPower"
              labelTranslationKey={t('step1:configVehicle:fiscalPower')}
              type="cv"
              minValue={1}
              isDisabled={isFormReadonly}
              maximumFractionDigits={0}
            />
          </Layout.Item>
          <Layout.Item default="1/5" s="1/1">
            <NumericFormField
              fieldName="dinHp"
              labelTranslationKey={t('step1:configVehicle:dinHp')}
              type="numeric"
              minValue={1}
              isDisabled={isFormReadonly}
              maximumFractionDigits={0}
            />
          </Layout.Item>
          <Layout.Item default="1/5" s="1/1">
            <NumericFormField
              fieldName="batteryPrice"
              type="money"
              maximumFractionDigits={2}
              labelTranslationKey="step1:configVehicle:batteryPrice"
              optional={!isEngineElectricOrHybrid(values?.engine)}
              isDisabled={!isEngineElectricOrHybrid(values?.engine) || isFormReadonly}
            />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/3" s="1/1">
            <BrCheckbox
              checked={values?.isDemo}
              name="demoCar"
              disabled={isFormReadonly}
              onChange={(e) => {
                setFieldValue('isDemo', e.target.checked);
              }}
            >
              {t('step1:configVehicle:demoCarCheckbox')}
            </BrCheckbox>
          </Layout.Item>
          {values.isDemo && (
            <Layout.Item default="2/3" s="1/1">
              <Fieldset>
                <Fieldset.Row>
                  <Layout>
                    <Layout.Item default="1/2" l="1/2" s="1/1">
                      <NumericFormField
                        fieldName="currMileage"
                        labelTranslationKey={t('step1:configVehicle:currMileage')}
                        type="km"
                        maximumFractionDigits={0}
                        onValueChange={(currMileage: number) =>
                          checkCurrentMileageStockAndNew(currMileage, setShowDemoCarModal)
                        }
                        allowEmpty
                        isDisabled={isFormReadonly}
                      />
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>
              </Fieldset>
            </Layout.Item>
          )}
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
